import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { updateUserStreak } from "../../../auth/data-access/store/authSlice";
import {
  selectLanguage,
  selectTranslations,
} from "../../../shared/data-access/store/general/generalSlice";
import { checkTranslationKey } from "../../../utils/translation";
import { StreakState } from "../../../shared/interfaces/streak.types";
import { Modal } from "../../../shared/ui/modal";
import { Button } from "../../../shared/ui/button";
import { useMarkStreakModalSeenMutation } from "../../data-access/store/services/streak.service";
import "./streak-modal.scss";

export interface StreakModalProps {
  streak?: StreakState | null;
  /** Sends the subscriber into gameplay, where the recovery condition is actually met */
  onPlay: () => void;
}

/**
 * FR-31 / FR-32: the break and recovery modals. One component, because the two differ only in
 * their title, flame, body and action - `pending_modal` decides which of them is showing.
 *
 * Every exit acknowledges the modal so it does not return on the next app open. That call clears
 * the flag and nothing else, so dismissing a break never costs the remaining recovery window.
 */
export function StreakModal({ streak, onPlay }: StreakModalProps) {
  const [dismissed, setDismissed] = useState(false);
  const [markSeen] = useMarkStreakModalSeenMutation();
  const dispatch = useAppDispatch();
  const translations = useAppSelector(selectTranslations);
  const lang = useAppSelector(selectLanguage);

  const variant = streak?.pending_modal ?? null;
  // The window can close before the subscriber ever opens the app, in which case the break modal
  // is only an explanation - there is nothing left to recover
  const canRecover = variant === "broken" && streak?.recovery != null;
  const lostCount =
    streak?.lost_count ?? streak?.recovery?.broken_from_count ?? 0;

  const dismiss = () => {
    setDismissed(true);
    // The response is the fresh state, so folding it back in keeps the rest of the app in step
    markSeen()
      .unwrap()
      .then((state) => dispatch(updateUserStreak(state)))
      .catch(() => {
        // The flag is still set server-side, so the modal simply returns on the next app open
      });
  };

  const handleAction = () => {
    dismiss();

    if (variant === "broken") {
      onPlay();
    }
  };

  // Nothing to acknowledge: render nothing at all rather than leaving a hidden full-screen
  // overlay mounted over the page it sits on
  if (variant === null || dismissed) {
    return null;
  }

  return (
    <Modal show setShow={dismiss}>
      <div className={`streak-modal streak-modal-${variant ?? "broken"}`}>
        <div className="streak-modal-body">
          <p className="streak-modal-title">
            {variant === "recovered"
              ? checkTranslationKey(
                  translations[lang]?.Streak_recovered,
                  "Streak recovered!"
                )
              : checkTranslationKey(
                  translations[lang]?.Your_streak_ended,
                  "Your streak ended"
                )}
          </p>

          <img
            className="streak-modal-flame"
            src={
              variant === "recovered"
                ? "/img/streak/flame-modal.png"
                : "/img/streak/flame-broken.png"
            }
            alt=""
          />

          <p className="streak-modal-text">
            {variant === "recovered" ? (
              <>
                {checkTranslationKey(
                  translations[lang]?.Your_streak_is_back_at,
                  "Your streak is back at"
                )}{" "}
                <strong>
                  {streak?.current}{" "}
                  {checkTranslationKey(translations[lang]?.days, "days")}.
                </strong>{" "}
                {checkTranslationKey(
                  translations[lang]?.Check_in_earlier_tomorrow,
                  "Check in earlier tomorrow to keep it going."
                )}
              </>
            ) : canRecover ? (
              <>
                {checkTranslationKey(
                  translations[lang]?.You_missed_a_day_and_your,
                  "You missed a day and your"
                )}{" "}
                {lostCount}
                {checkTranslationKey(
                  translations[lang]?.day_streak_ended_recover_it_now_with_a,
                  "-day streak ended. Recover it now with a"
                )}{" "}
                <strong>
                  {checkTranslationKey(
                    translations[lang]?.One_day_penalty,
                    "1-day penalty."
                  )}
                </strong>
              </>
            ) : (
              <>
                {checkTranslationKey(
                  translations[lang]?.You_missed_a_day_and_your,
                  "You missed a day and your"
                )}{" "}
                {lostCount}
                {checkTranslationKey(
                  translations[lang]?.day_streak_ended,
                  "-day streak ended."
                )}{" "}
                <strong>
                  {checkTranslationKey(
                    translations[lang]?.Play_today_to_start_a_new_one,
                    "Play today to start a new one."
                  )}
                </strong>
              </>
            )}
          </p>
        </div>

        <div className="streak-modal-footer">
          <Button buttonType="success-play" onClick={handleAction}>
            {variant === "recovered"
              ? checkTranslationKey(translations[lang]?.OK, "OK")
              : canRecover
              ? checkTranslationKey(
                  translations[lang]?.Recover_my_streak,
                  "Recover my streak"
                )
              : checkTranslationKey(translations[lang]?.Play_now, "Play now!")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default StreakModal;
